<template>
    <div class="my-feedback-page animation-fadeIn">
        <div class="page-header-container">
            <div class="page-header">
                <h2 class="page-title">我的反馈</h2>
                <p class="page-subtitle">提交反馈建议，查看与管理员的沟通记录</p>
            </div>
        </div>

        <div class="content-body">
            <!-- New Feedback Button -->
            <div class="top-actions">
                <button class="new-feedback-btn" @click="showNewDialog = true">
                    <span class="btn-icon">+</span>
                    <span>提交新反馈</span>
                </button>
            </div>

            <!-- Feedback List -->
            <div v-loading="loading">
                <el-empty v-if="feedbacks.length === 0 && !loading" description="暂无反馈记录，点击上方按钮提交第一条反馈" />

                <div v-for="fb in feedbacks" :key="fb.id" class="feedback-card" :class="{ 'has-unread': fb.hasUnread }"
                    @click="openChat(fb)">
                    <div class="fb-header">
                        <div class="fb-meta">
                            <el-tag :type="statusType(fb.status)" size="small" effect="plain">{{ statusLabel(fb.status)
                            }}</el-tag>
                            <span class="fb-type">{{ fb.type }}</span>
                            <el-tag v-if="fb.hasUnread" type="danger" size="small" effect="dark"
                                style="margin-left:6px;">新回复</el-tag>
                        </div>
                        <span class="fb-date">{{ formatTime(fb.createdAt) }}</span>
                    </div>
                    <div class="fb-title">{{ fb.title || fb.type }}</div>
                    <div class="fb-hint">
                        <span v-if="fb.questionId">题目 #{{ fb.questionId }}</span>
                        <i class="fas fa-chevron-right" style="color:#ccc; margin-left:auto;"></i>
                    </div>
                </div>
            </div>
        </div>

        <!-- New Feedback Dialog -->
        <el-dialog v-model="showNewDialog" title="提交新反馈" width="500px" append-to-body destroy-on-close>
            <el-form label-position="top">
                <el-form-item label="反馈类型">
                    <el-radio-group v-model="newForm.type">
                        <el-radio label="答案错误">答案错误</el-radio>
                        <el-radio label="解析错误">解析错误</el-radio>
                        <el-radio label="题目不完整">题目不完整</el-radio>
                        <el-radio label="选项错误">选项错误</el-radio>
                        <el-radio label="功能建议">功能建议</el-radio>
                        <el-radio label="其他">其他</el-radio>
                    </el-radio-group>
                </el-form-item>
                <el-form-item label="题目ID（选填）">
                    <el-input v-model.number="newForm.questionId" type="number" placeholder="如果是题目纠错，请填写题目编号" />
                </el-form-item>
                <el-form-item label="反馈内容">
                    <el-input v-model="newForm.content" type="textarea" :rows="4" placeholder="请详细描述您的反馈..." />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="showNewDialog = false">取消</el-button>
                <el-button type="primary" :disabled="!newForm.type || !newForm.content" :loading="submitting"
                    @click="submitNew">提交</el-button>
            </template>
        </el-dialog>

        <!-- Chat Dialog -->
        <el-dialog v-model="showChatDialog" :title="chatTitle" width="600px" append-to-body destroy-on-close
            class="chat-dialog">
            <div v-loading="loadingChat" class="chat-container" ref="chatContainerRef">
                <div v-for="msg in chatMessages" :key="msg.id" class="chat-bubble-row"
                    :class="{ 'is-mine': msg.senderRole === 'user' }">
                    <div class="chat-avatar">
                        <el-avatar :size="32"
                            :style="msg.senderRole === 'admin' ? 'background:#409eff' : 'background:#67c23a'">
                            {{ msg.senderRole === 'admin' ? '管' : '我' }}
                        </el-avatar>
                    </div>
                    <div class="chat-bubble" :class="msg.senderRole === 'admin' ? 'admin-bubble' : 'user-bubble'">
                        <div class="bubble-name">{{ msg.senderRole === 'admin' ? (msg.senderName || '管理员') : '我' }}
                            <span class="bubble-time">{{ formatTime(msg.createdAt) }}</span>
                        </div>
                        <div class="bubble-content">{{ msg.content }}</div>
                    </div>
                </div>
            </div>

            <template #footer>
                <div class="chat-footer">
                    <el-input v-model="replyContent" type="textarea" :rows="2" placeholder="输入回复内容..."
                        @keyup.ctrl.enter="sendReply" />
                    <div class="chat-footer-actions">
                        <el-button type="primary" :disabled="!replyContent.trim()" :loading="sending"
                            @click="sendReply">
                            发送 <span style="font-size:11px; opacity:0.7; margin-left:4px;">(Ctrl+Enter)</span>
                        </el-button>
                    </div>
                </div>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import api from '@/utils/api'

const loading = ref(false)
const feedbacks = ref([])

// New feedback form
const showNewDialog = ref(false)
const submitting = ref(false)
const newForm = ref({ type: '', questionId: null, content: '' })

// Chat
const showChatDialog = ref(false)
const loadingChat = ref(false)
const chatMessages = ref([])
const chatTitle = ref('')
const currentFeedbackId = ref(null)
const replyContent = ref('')
const sending = ref(false)
const chatContainerRef = ref(null)

const fetchFeedbacks = async () => {
    loading.value = true
    try {
        const res = await api.get('/api/feedbacks/mine')
        feedbacks.value = res.data.feedbacks || []
    } catch (err) {
        ElMessage.error('加载反馈失败')
    } finally {
        loading.value = false
    }
}

const submitNew = async () => {
    submitting.value = true
    try {
        await api.post('/api/feedbacks', {
            type: newForm.value.type,
            questionId: newForm.value.questionId || 0,
            content: newForm.value.content
        })
        ElMessage.success('反馈提交成功')
        showNewDialog.value = false
        newForm.value = { type: '', questionId: null, content: '' }
        fetchFeedbacks()
    } catch (err) {
        ElMessage.error('提交失败')
    } finally {
        submitting.value = false
    }
}

const openChat = async (fb) => {
    currentFeedbackId.value = fb.id
    chatTitle.value = `${fb.type} — ${fb.title || ''}`
    showChatDialog.value = true
    loadingChat.value = true
    chatMessages.value = []
    replyContent.value = ''

    try {
        const res = await api.get(`/api/feedbacks/${fb.id}`)
        chatMessages.value = res.data.feedback.messages || []
        fb.hasUnread = false // Mark as read on frontend
        await nextTick()
        scrollToBottom()
    } catch (err) {
        ElMessage.error('加载对话失败')
    } finally {
        loadingChat.value = false
    }
}

const sendReply = async () => {
    if (!replyContent.value.trim()) return
    sending.value = true
    try {
        const res = await api.post(`/api/feedbacks/${currentFeedbackId.value}/messages`, {
            content: replyContent.value
        })
        chatMessages.value.push(res.data.msg)
        replyContent.value = ''
        await nextTick()
        scrollToBottom()
    } catch (err) {
        ElMessage.error('发送失败')
    } finally {
        sending.value = false
    }
}

const scrollToBottom = () => {
    if (chatContainerRef.value) {
        chatContainerRef.value.scrollTop = chatContainerRef.value.scrollHeight
    }
}

const statusType = (s) => {
    if (s === 'pending') return 'warning'
    if (s === 'replied') return 'success'
    if (s === 'rejected') return 'danger'
    if (s === 'resolved') return 'info'
    return 'info'
}
const statusLabel = (s) => {
    if (s === 'pending') return '待处理'
    if (s === 'replied') return '已回复'
    if (s === 'rejected') return '已驳回'
    if (s === 'resolved') return '已解决'
    return s
}

const formatTime = (ts) => {
    if (!ts) return ''
    const d = new Date(ts * 1000)
    const now = new Date()
    const isToday = d.toDateString() === now.toDateString()
    const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    if (isToday) return `今天 ${time}`
    return `${d.getMonth() + 1}/${d.getDate()} ${time}`
}

onMounted(fetchFeedbacks)
</script>

<style scoped>
.my-feedback-page {
    min-height: 100vh;
    background: #f8fafc;
}

.page-header-container {
    background: linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%);
    padding: 50px 40px;
    margin-bottom: -30px;
}

.page-header {
    max-width: 1200px;
    margin: 0 auto;
}

.page-title {
    font-size: 32px;
    font-weight: 800;
    color: #fff;
    margin: 0;
}

.page-subtitle {
    font-size: 15px;
    color: #bfdbfe;
    margin-top: 8px;
}

.content-body {
    max-width: 900px;
    margin: 0 auto;
    padding: 0 40px 40px;
}

.top-actions {
    margin-bottom: 20px;
    text-align: right;
}

.new-feedback-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 28px;
    background: linear-gradient(135deg, #2563eb 0%, #7c3aed 100%);
    color: #fff;
    border: none;
    border-radius: 12px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s;
    box-shadow: 0 4px 14px rgba(37, 99, 235, 0.3);
}

.new-feedback-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(37, 99, 235, 0.45);
}

.new-feedback-btn:active {
    transform: translateY(0);
}

.btn-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    background: rgba(255, 255, 255, 0.25);
    border-radius: 6px;
    font-size: 16px;
    font-weight: 700;
}

.feedback-card {
    background: #fff;
    border-radius: 16px;
    padding: 20px 24px;
    margin-bottom: 12px;
    border: 1px solid #f0f0f0;
    cursor: pointer;
    transition: all 0.25s;
}

.feedback-card:hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
    transform: translateY(-1px);
}

.feedback-card.has-unread {
    border-left: 4px solid #f56c6c;
}

.fb-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.fb-meta {
    display: flex;
    align-items: center;
    gap: 8px;
}

.fb-type {
    font-weight: 600;
    font-size: 14px;
    color: #303133;
}

.fb-date {
    font-size: 12px;
    color: #999;
}

.fb-title {
    font-size: 15px;
    color: #1e293b;
    font-weight: 500;
    margin-bottom: 6px;
}

.fb-hint {
    font-size: 12px;
    color: #94a3b8;
    display: flex;
    align-items: center;
}

/* Chat Dialog */
.chat-container {
    max-height: 400px;
    overflow-y: auto;
    padding: 16px;
    background: #f5f7fa;
    border-radius: 12px;
    margin-bottom: 12px;
}

.chat-bubble-row {
    display: flex;
    margin-bottom: 16px;
    gap: 10px;
}

.chat-bubble-row.is-mine {
    flex-direction: row-reverse;
}

.chat-avatar {
    flex-shrink: 0;
}

.chat-bubble {
    max-width: 70%;
    padding: 10px 14px;
    border-radius: 12px;
    font-size: 14px;
    line-height: 1.5;
}

.admin-bubble {
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 2px 12px 12px 12px;
}

.user-bubble {
    background: #e1f3d8;
    border-radius: 12px 2px 12px 12px;
}

.bubble-name {
    font-size: 12px;
    color: #999;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.bubble-time {
    font-size: 11px;
    color: #c0c4cc;
}

.bubble-content {
    color: #303133;
    white-space: pre-wrap;
    word-break: break-word;
}

.chat-footer {
    width: 100%;
}

.chat-footer-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 10px;
}

.animation-fadeIn {
    animation: fadeIn 0.6s ease;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(15px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
